import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { useContext, useMemo, useState } from "react";

import { ToastContext } from "../../../../context/ToastContext";
import {
  useUploadAddendumBSourceMutation,
  useUploadAddendumAASourceMutation,
  useUploadClfsSourceMutation,
  useUploadAspSourceMutation,
  useUploadDmeposSourceMutation,
  useUploadPprvuSourceMutation,
  useUploadGpciSourceMutation,
  useUploadIppsSourceMutation,
  useUploadIppsStdAmountsSourceMutation,
  useUploadIppsImpactSourceMutation,
  useUploadAscWageIndexSourceMutation,
  useFetchPsfInpatientSourceMutation,
} from "../../../../store/queries/baseRateFiles";
import { ISourceDataStatus } from "../../../../types/baseRateFile";
import { StyledDialog } from "../styled";
import FileUploadEntryComponent, {
  IFileUploadEntry,
  isApiFetchSource,
} from "./components/FileUploadEntryComponent";
import SourceDataTable from "./components/SourceDataTable";

const DATA_SET_LABELS: Record<keyof ISourceDataStatus, string> = {
  addendumB: "Addendum B",
  addendumAA: "Addendum AA",
  clfs: "CLFS",
  asp: "ASP",
  dmepos: "DMEPOS",
  pprvu: "MPFS (PPRRVU)",
  gpci: "GPCI Addendum E (Locality Data)",
  ipps: "IPPS Table 5 FR (MS-DRG Data)",
  ippsStdAmounts: "IPPS Tables 1A-1E (Standardized Amounts)",
  ippsImpact: "IPPS Impact File (Hospital Data)",
  ippsPsfInpatient: "IPPS PSF Inpatient (CMS API)",
  ascWageIndex: "ASC Wage Index",
};

interface IUploadSourceFilesDialogNewProps {
  visible: boolean;
  onHide: () => void;
  sourceDataStatus: ISourceDataStatus | null;
  onSuccess?: () => void;
}

const createNewEntry = (): IFileUploadEntry => ({
  id: crypto.randomUUID(),
  year: new Date().getFullYear(),
  fileType: null,
  file: null,
});

function UploadSourceFilesDialogNew({
  visible,
  onHide,
  sourceDataStatus,
  onSuccess,
}: IUploadSourceFilesDialogNewProps) {
  const { current: toastElement } = useContext(ToastContext);
  const [showInitialUploadForm, setShowInitialUploadForm] = useState(false);
  const [showAdditionalUploadForm, setShowAdditionalUploadForm] =
    useState(false);
  const [entries, setEntries] = useState<IFileUploadEntry[]>([
    createNewEntry(),
  ]);
  const [isUploading, setIsUploading] = useState(false);

  const [uploadAddendumB] = useUploadAddendumBSourceMutation();
  const [uploadAddendumAA] = useUploadAddendumAASourceMutation();
  const [uploadClfs] = useUploadClfsSourceMutation();
  const [uploadAsp] = useUploadAspSourceMutation();
  const [uploadDmepos] = useUploadDmeposSourceMutation();
  const [uploadPprvu] = useUploadPprvuSourceMutation();
  const [uploadGpci] = useUploadGpciSourceMutation();
  const [uploadIpps] = useUploadIppsSourceMutation();
  const [uploadIppsStdAmounts] = useUploadIppsStdAmountsSourceMutation();
  const [uploadIppsImpact] = useUploadIppsImpactSourceMutation();
  const [uploadAscWageIndex] = useUploadAscWageIndexSourceMutation();
  const [fetchPsfInpatient] = useFetchPsfInpatientSourceMutation();

  /**
   * File-upload sources expect (file, year) and return a row count.
   * API-fetch sources (like PSF) only need year. We branch in handleUpload.
   */
  const getUploadMutationForFileType = (fileType: keyof ISourceDataStatus) => {
    const mutationMap: Partial<
      Record<
        keyof ISourceDataStatus,
        (args: {
          file: File;
          year: number;
        }) => ReturnType<typeof uploadAddendumB>
      >
    > = {
      addendumB: uploadAddendumB,
      addendumAA: uploadAddendumAA,
      clfs: uploadClfs,
      asp: uploadAsp,
      dmepos: uploadDmepos,
      pprvu: uploadPprvu,
      gpci: uploadGpci,
      ipps: uploadIpps,
      ippsStdAmounts: uploadIppsStdAmounts,
      ippsImpact: uploadIppsImpact,
      ascWageIndex: uploadAscWageIndex,
    };
    return mutationMap[fileType];
  };

  const isDataEmpty = useMemo(() => {
    if (!sourceDataStatus) return true;

    // Check if all arrays in sourceDataStatus are empty
    return (
      sourceDataStatus.addendumB.length === 0 &&
      sourceDataStatus.addendumAA.length === 0 &&
      sourceDataStatus.clfs.length === 0 &&
      sourceDataStatus.asp.length === 0 &&
      sourceDataStatus.dmepos.length === 0 &&
      sourceDataStatus.pprvu.length === 0 &&
      (sourceDataStatus.gpci?.length ?? 0) === 0 &&
      (sourceDataStatus.ipps?.length ?? 0) === 0 &&
      (sourceDataStatus.ippsStdAmounts?.length ?? 0) === 0 &&
      (sourceDataStatus.ippsImpact?.length ?? 0) === 0 &&
      (sourceDataStatus.ippsPsfInpatient?.length ?? 0) === 0 &&
      (sourceDataStatus.ascWageIndex?.length ?? 0) === 0
    );
  }, [sourceDataStatus]);

  const missingFileTypes = useMemo(() => {
    if (!sourceDataStatus || isDataEmpty) return [];

    const missing: string[] = [];
    (Object.keys(DATA_SET_LABELS) as (keyof ISourceDataStatus)[]).forEach(
      (key) => {
        if (
          !sourceDataStatus[key]?.length ||
          sourceDataStatus[key].length === 0
        ) {
          missing.push(DATA_SET_LABELS[key]);
        }
      }
    );
    return missing;
  }, [sourceDataStatus, isDataEmpty]);

  const handleCloseDialog = () => {
    if (isUploading) return;
    setShowInitialUploadForm(false);
    setShowAdditionalUploadForm(false);
    setEntries([createNewEntry()]);
    onHide();
  };

  const handleUpload = async () => {
    // Validate: every entry needs year + fileType. File is required for upload
    // sources; API-fetch sources (e.g. ippsPsfInpatient) need no file.
    const allEntriesValid = entries.every(
      (entry) =>
        entry.year !== null &&
        entry.fileType !== null &&
        (isApiFetchSource(entry.fileType) || entry.file !== null)
    );
    if (!allEntriesValid) {
      return;
    }

    setIsUploading(true);

    try {
      const taskPromises = entries.map((entry) => {
        // PSF and any future fetch-based sources go through the dedicated mutation.
        if (entry.fileType && isApiFetchSource(entry.fileType)) {
          if (entry.fileType === "ippsPsfInpatient") {
            return fetchPsfInpatient({
              year: entry.year!,
              quarter: entry.quarter,
            }).unwrap();
          }
          // Defensive: shouldn't reach here unless we add another fetch source without wiring.
          return Promise.reject(
            new Error(
              `No fetch handler wired for source type ${entry.fileType}`
            )
          );
        }
        // Upload-based sources
        const mutation = getUploadMutationForFileType(
          entry.fileType as keyof ISourceDataStatus
        );
        if (!mutation || !entry.file) {
          return Promise.reject(
            new Error(`No upload handler for source type ${entry.fileType}`)
          );
        }
        return mutation({ file: entry.file, year: entry.year! }).unwrap();
      });

      await Promise.all(taskPromises);

      toastElement?.show({
        severity: "success",
        summary: "Success",
        detail: "Source files uploaded successfully.",
      });

      // Reset and close dialog on success
      setShowInitialUploadForm(false);
      setShowAdditionalUploadForm(false);
      setEntries([createNewEntry()]);
      onSuccess?.();
      onHide();
    } catch (error: any) {
      console.error("Failed to upload source files:", error);
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: error?.data?.messages[0] || "Failed to upload source files.",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleEntryChange = (updatedEntry: IFileUploadEntry) => {
    setEntries((prev) =>
      prev.map((entry) => (entry.id === updatedEntry.id ? updatedEntry : entry))
    );
  };

  const handleRemoveEntry = (id: string) => {
    setEntries((prev) => prev.filter((entry) => entry.id !== id));
  };

  const handleAddMore = () => {
    setEntries((prev) => [...prev, createNewEntry()]);
  };

  const hasValidEntries = entries.every(
    (entry) =>
      entry.year &&
      entry.fileType &&
      (isApiFetchSource(entry.fileType) || entry.file)
  );

  const renderContent = () => {
    // Empty state - no data at all
    if (isDataEmpty && !showInitialUploadForm) {
      return (
        <div
          className="empty-state"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "3rem",
            textAlign: "center",
          }}
        >
          <i
            className="pi pi-inbox"
            style={{
              fontSize: "3rem",
              color: "var(--text-color-secondary)",
              marginBottom: "1rem",
            }}
          />
          <h3 style={{ margin: "0 0 0.5rem 0" }}>No Source Data Available</h3>
          <p style={{ margin: 0, color: "var(--text-color-secondary)" }}>
            There is no source data uploaded yet.
          </p>
          <Button
            label="Upload source files"
            icon="pi pi-upload"
            style={{ marginTop: "1.5rem" }}
            onClick={() => setShowInitialUploadForm(true)}
          />
        </div>
      );
    }

    // Initial upload form - when data is empty and user clicked upload
    if (isDataEmpty && showInitialUploadForm) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            padding: "1rem",
          }}
        >
          {entries.map((entry) => (
            <FileUploadEntryComponent
              key={entry.id}
              entry={entry}
              onChange={handleEntryChange}
              onRemove={() => handleRemoveEntry(entry.id)}
              showRemove={entries.length > 1}
            />
          ))}

          <Button
            label="Add more"
            icon="pi pi-plus"
            className="p-button-text p-0"
            onClick={handleAddMore}
            style={{ alignSelf: "flex-end" }}
          />
        </div>
      );
    }

    // Data exists - show table with optional additional upload form
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        {!showAdditionalUploadForm && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              label="Upload/replace files"
              icon="pi pi-upload"
              className="p-button-text p-0"
              onClick={() => setShowAdditionalUploadForm(true)}
            />
          </div>
        )}

        {showAdditionalUploadForm && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
            }}
          >
            {entries.map((entry) => (
              <FileUploadEntryComponent
                key={entry.id}
                entry={entry}
                onChange={handleEntryChange}
                onRemove={() => handleRemoveEntry(entry.id)}
                showRemove={entries.length > 1}
              />
            ))}
            <Button
              label="Add more"
              icon="pi pi-plus"
              className="p-button-text p-0"
              onClick={handleAddMore}
              style={{ alignSelf: "flex-end" }}
            />
          </div>
        )}

        <SourceDataTable sourceDataStatus={sourceDataStatus} />
      </div>
    );
  };

  const renderFooter = () => {
    if (!showInitialUploadForm && !showAdditionalUploadForm) return null;

    return (
      <div className="flex justify-content-end gap-2">
        <Button
          label="Cancel"
          icon="pi pi-times"
          className="p-button-outlined"
          onClick={handleCloseDialog}
          disabled={isUploading}
        />
        <Button
          label={isUploading ? "Uploading..." : "Upload"}
          icon={isUploading ? "pi pi-spin pi-spinner" : "pi pi-upload"}
          disabled={!hasValidEntries || isUploading}
          loading={isUploading}
          onClick={handleUpload}
        />
      </div>
    );
  };

  return (
    <StyledDialog
      header="Upload Source Files"
      visible={visible}
      onHide={handleCloseDialog}
      style={{ width: "900px" }}
      footer={renderFooter}
      closable={!isUploading}
      closeOnEscape={!isUploading}
    >
      {missingFileTypes.length > 0 && (
        <Message
          severity="warn"
          text={`The following files are missing: ${missingFileTypes.join(
            ", "
          )}`}
          style={{ width: "100%", marginBottom: "1rem" }}
        />
      )}
      {renderContent()}
    </StyledDialog>
  );
}

export default UploadSourceFilesDialogNew;
